import React, { useEffect, createContext, useContext } from "react";

import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { useUserRelationships } from "~/hooks/queries/use-user-relationships";
import { initAnalytics } from "~/lib/analytics";

// AIDEV-NOTE: Analytics provider that initializes analytics with user context from Supabase session

const AnalyticsContext = createContext<{}>({});

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { session } = useSupabaseSession();
    const userRelationships = useUserRelationships();
    const isPractitioner = userRelationships.data?.isPractitioner;
    // AIDEV-NOTE: Normalize userType for analytics super-properties and profiles
    const calculatedUserType: "client" | "practitioner" | "unknown" =
        isPractitioner
            ? "practitioner"
            : (userRelationships.data?.practitioners?.length || 0) > 0
              ? "client"
              : "unknown";
    // Avoid setting a noisy "unknown" userType in analytics
    const userType =
        calculatedUserType === "unknown" ? undefined : calculatedUserType;

    useEffect(() => {
        // AIDEV-NOTE: Initialize and update analytics with user and userType (future-proof)
        if (session?.user) {
            initAnalytics(session.user, { userType });
        }
    }, [session?.user, userType]);

    return (
        <AnalyticsContext.Provider value={{}}>
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error(
            "useAnalytics must be used within an AnalyticsProvider",
        );
    }
    return context;
};
